import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import { parseDateToTimeZoneWithTime } from "../../helpers/parseDate";
import { stableSort, getSorting } from "../../helpers/materialTableFunctions";
import { showModal } from "../../actions/actionCreators";

import TableHeader from "../material-components/Table/TableHeader";
import TableBody from "../material-components/Table/TableBody";
import TableFooter from "../material-components/Table/TableFooter";
import LoadingTable from "../material-components/WidgetSkeleton/LoadingTable";

// importing material-ui library
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

const ImportFieldStatus = ({ widget, filteredImportSummaries }) => {
    const dispatch = useDispatch();

    // State
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("LastRun");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [sortedData, setSortedData] = useState([]); //for table data
    // Redux
    const loading = false;
    const timeZoneIana = useSelector(state => state.session.userSettings["time-zone-iana"]);

    useEffect(() => {
        setPage(0);
    }, [filteredImportSummaries]);

    useEffect(() => {
        setSortedData(stableSort(filteredImportSummaries, getSorting(order, orderBy)));
    }, [filteredImportSummaries, order, orderBy]);

    const formatDate = date => (date ? parseDateToTimeZoneWithTime(date, timeZoneIana) : null);

    const handleRequestSort = (event, property) => {
        setOrder(property !== orderBy || order === "desc" ? "asc" : "desc");
        setOrderBy(property);
    };

    const viewLogButton = item => (
        <Tooltip title="View full action log">
            <IconButton aria-label="log" color="secondary" size="small" onClick={() => openLog(item.Id)}>
                <i className="material-icons" style={{ height: "15px", width: "15px", fontSize: "15px" }}>
                    list
                </i>
            </IconButton>
        </Tooltip>
    );

    const openLog = id => {
        const modalProps = {
            logId: id,
        };
        dispatch(showModal("IMPORT_BULK_LOG_DETAIL_MODAL", modalProps));
    };

    const footer = () => {
        if (filteredImportSummaries.length > 0) {
            return (
                <TableFooter
                    displayDense={false}
                    displayPaging={true}
                    rowsPerPageOptions={!widget ? [5, 10, 15, 20, 25] : [5]}
                    count={filteredImportSummaries.length}
                    rowsPerPage={!widget ? rowsPerPage : 5}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={
                        !widget
                            ? e => {
                                  setRowsPerPage(parseInt(e.target.value, 10));
                                  setPage(0);
                              }
                            : null
                    }
                />
            );
        } else {
            return null;
        }
    };

    const emptyMessage = ["EmptyMessage", "No Import Log to Display.", "smDown"];

    const tableHeader = [
        ["Status", "Status", "smDown", "left"],
        ["Message", "Message", "smDown", "center"],
        ["ImportUserId", "User", "smDown", "center"],
        ["ImportStartDate", "Start", "smDown", "center"],
        ["ImportEndDate", "End", "smDown", "center"],
        ["Id", "Detail", "smDown", "center"],
    ];

    const tableData = [];
    sortedData.map(dataRow => {
        tableData.push([
            ["Status", dataRow.Status == 1 ? "Success" : "Fail", "smDown", "left"],
            ["Message", dataRow.Message, "smDown", "left"],
            ["ImportUserId", dataRow.ImportedBy, "smDown", "center"],
            ["ImportStartDate", formatDate(dataRow.ImportStartDate), "smDown", "left"],
            ["ImportEndDate", formatDate(dataRow.ImportEndDate), "smDown", "left"],
            ["Id", viewLogButton(dataRow), "smDown", "right"],
        ]);
    });

    return (
        <div>
            <Paper>
                <Table size={"medium"}>
                    <TableHeader
                        style={{ fontSize: "14px" }}
                        align="center"
                        headers={tableHeader}
                        ignoreHeaders={["Id"]}
                        orderBy={orderBy}
                        order={order}
                        onClick={handleRequestSort}
                        widget={widget}
                    />
                    {loading.scheduleStatus ? (
                        <LoadingTable cols={6} rows={widget ? 5 : 10} />
                    ) : (
                        <TableBody
                            data={tableData}
                            page={page}
                            rowsPerPage={!widget ? rowsPerPage : 5}
                            emptyMessage={emptyMessage}
                            widget={widget}
                            style={{ fontSize: "14px" }}
                        />
                    )}
                </Table>
                {!widget && footer()}
            </Paper>
            {widget && footer()}
        </div>
    );
};

ImportFieldStatus.propTypes = {
    widget: PropTypes.bool,
    filteredImportSummaries: PropTypes.array,
};

export default ImportFieldStatus;
